import { getSessionToken } from "@shopify/app-bridge-utils"

const SESSION_TOKEN_TIMEOUT_MS = 25_000
/** token 剩余有效期少于此秒数时视为"即将过期"，不再复用 */
const TOKEN_EXPIRY_BUFFER_SECS = 19

type AppBridgeForSession = Parameters<typeof getSessionToken>[0]

const readUrlToken = (): { idToken: string | null; session: string | null } => {
  const params = new URLSearchParams(window.location.search)

  return {
    idToken: params.get("id_token"),
    session: params.get("session"),
  }
}

const decodeJwtExp = (token: string): number | null => {
  try {
    const part = token.split(".")[1]

    if (!part) {
      return null
    }
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"))
    const payload = JSON.parse(json) as Record<string, unknown>

    return typeof payload.exp === "number" ? payload.exp : null
  } catch {
    return null
  }
}

/** token 是否仍有效（距过期超过 buffer 秒） */
const isJwtFresh = (token: string): boolean => {
  const exp = decodeJwtExp(token)

  if (exp === null) {
    return false
  }
  return Date.now() / 1000 < exp - TOKEN_EXPIRY_BUFFER_SECS
}

/**
 * 获取 Shopify 请求用的 Bearer token。
 *
 * 优先级：
 * 1. URL `id_token` 仍在有效期 → 直接返回（首次加载快速路径，forceRefresh 时跳过）
 * 2. `window.shopify.idToken()` → App Bridge v4，在 app window / popup 中也能正常工作
 * 3. App Bridge v3 `getSessionToken(app)` + 超时 → postMessage 在 popup 中可能超时
 * 4. URL `session` 参数 → 最后兜底
 *
 * @param forceRefresh 为 true 时跳过 URL token 缓存，强制从 App Bridge 获取最新 token
 *                     适用于长耗时请求（如文件上传），确保 token 有效期尽量长
 */
export const getSessionTokenWithFallback = (
  app: AppBridgeForSession,
  { forceRefresh = false }: { forceRefresh?: boolean } = {},
): Promise<string> => {
  const { idToken, session } = readUrlToken()

  if (!forceRefresh && idToken && isJwtFresh(idToken)) {
    return Promise.resolve(idToken)
  }

  if (typeof window.shopify?.idToken === "function") {
    return window.shopify.idToken()
  }

  return Promise.race([
    getSessionToken(app),
    new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error("Shopify getSessionToken timeout"))
      }, SESSION_TOKEN_TIMEOUT_MS)
    }),
  ]).catch((err: unknown) => {
    if (session) {
      if (import.meta.env.DEV) {
        console.debug("[shopifySessionToken] getSessionToken 超时，改用 URL session 参数", err)
      }
      return session
    }

    if (import.meta.env.DEV) {
      console.debug("[shopifySessionToken] getSessionToken 超时且无 URL 后备", err)
    }

    return Promise.reject(err)
  })
}

/**
 * 获取 Shopify idToken，供 `@channelwill/components` 的中台 SDK 注入鉴权头。
 *
 * 与 {@link getSessionTokenWithFallback} 的区别：组件包在模块级调用这个 getter，
 * 拿不到 App Bridge v3 实例，因此只能走 URL token 与 App Bridge v4 两条路。
 *
 * 优先级：
 * 1. URL `id_token` 仍在有效期 → 直接返回
 * 2. `window.shopify.idToken()` → App Bridge v4
 *
 * 取不到时返回空串而不是抛错：组件包约定接口失败时渲染 null，不应把异常抛回宿主。
 */
export const getShopifyIdToken = async (): Promise<string> => {
  const { idToken } = readUrlToken()

  if (idToken && isJwtFresh(idToken)) {
    return idToken
  }

  if (typeof window.shopify?.idToken === "function") {
    try {
      const idToken = await window.shopify.idToken()

      return idToken ?? ""
    } catch (err) {
      if (import.meta.env.DEV) {
        console.debug("[shopifySessionToken] window.shopify.idToken() 失败", err)
      }
    }
  }

  return ""
}
